<script lang="ts">
    import { reactive } from 'vue';
    import vm, { GridType } from '@journeys/Journeys.viewmodel';

    // util
    // import { deepClone, bitshift, uid } from '@utils'

    // components
    import ContainerFloating from '../../../shared/components/ContainerFloating.vue';
    import FluidButton from '@cwd/shared/components/FluidButton.vue';
    import InputCheckbox from '@shared/components/InputCheckbox.vue';

    // assets
    import {
        // MagnetSVG,
        DarkThemeSVG,
        LightThemeSVG,
        OptionsSVG,
        ArrowLeftSVG,
        ArrowRightSVG,
        GridSolidSVG,
        GridPatternSVG,
        GridDotSVG,
        GridNoneSVG,
    } from '@shared/assets/vector/ui/IconsUI';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'EditorUI_Topbar',
    };

    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const { editor, settings, nav } = vm;

    const state = reactive({
        show_all: false,
    });

    const toggleSetting = (setting: string) => {
        switch (setting) {
            case 'grid-snap':
                settings.grid.snap = !settings.grid.snap;
                break;
            case 'dark-theme':
                settings.ui.darkTheme = !settings.ui.darkTheme;
        }
    };
</script>

<template>
    <ContainerFloating class="horizontal toolbar">
        <FluidButton class="btn btn-default" :icon="OptionsSVG()" @click="() => (state.show_all = !state.show_all)" />

        <template v-if="state.show_all">
            <div class="separator vertical"></div>

            <div class="settings-group horizontal fl">
                <span class="text-sm m-xs-r">{{ $Resources.SelectGridTypeHeading }}:</span>

                <FluidButton
                    :icon="GridNoneSVG()"
                    class="btn btn-default grid-type-btn"
                    :class="settings.grid.type === GridType.None && 'active-toggle'"
                    @click="() => settings.grid.set('type', GridType.None)" />
                <FluidButton
                    :icon="GridPatternSVG()"
                    class="btn btn-default grid-type-btn"
                    :class="settings.grid.type === GridType.Pattern && 'active-toggle'"
                    @click="() => settings.grid.set('type', GridType.Pattern)" />
                <FluidButton
                    :icon="GridSolidSVG()"
                    class="btn btn-default grid-type-btn"
                    :class="settings.grid.type === GridType.Solid && 'active-toggle'"
                    @click="() => settings.grid.set('type', GridType.Solid)" />
                <FluidButton
                    :icon="GridDotSVG()"
                    class="btn btn-default grid-type-btn"
                    :class="settings.grid.type === GridType.Dot && 'active-toggle'"
                    @click="() => settings.grid.set('type', GridType.Dot)" />
            </div>

            <div class="settings-group horizontal fl">
                <span class="text-sm m-xs-r m-sm-l">{{ $Resources.SnapHeading }}:</span>

                <InputCheckbox
                    class="input-checkbox-minimal m-sm-r"
                    :checked="settings.grid.snap"
                    @update:checked="(newValue) => (settings.grid.snap = newValue)">
                    <template #label>
                        <span class="sr-only">{{ $Resources.SnapLabel }}</span>
                    </template>
                </InputCheckbox>
            </div>

            <template v-if="editor.dev.show.admin_tools">
                <div class="separator vertical"></div>

                <div class="settings-group horizontal fl">
                    <h4 class="subheading">Grid size:</h4>

                    <ArrowLeftSVG class="control-arrow" @click="settings.grid.set('spacing', settings.grid.spacing - 1)" />
                    <h3>{{ settings.grid.spacing }}</h3>
                    <ArrowRightSVG class="control-arrow" @click="settings.grid.set('spacing', settings.grid.spacing + 1)" />
                </div>

                <div class="settings-group horizontal fl">
                    <h4 class="subheading">UI Scale:</h4>

                    <ArrowLeftSVG class="control-arrow" @click="settings.ui.setREM(-1)" />
                    <h3>{{ settings.ui.rem }}</h3>
                    <ArrowRightSVG class="control-arrow" @click="settings.ui.setREM(1)" />
                </div>

                <!-- for shits & giggles -->
                <div class="separator vertical"></div>
                <!-- trash -->
                <div class="trash" :class="settings.ui.darkTheme ? 'blackout' : ''"></div>
                <!-- literal trash -->

                <div class="settings-group horizontal fl mr-sm">
                    <component class="mh-xxs" :is="settings.ui.darkTheme ? LightThemeSVG : DarkThemeSVG"></component>

                    <div class="checkbox-trash dark" :class="settings.ui.darkTheme && 'checked'" @click="toggleSetting('dark-theme')">
                        <div class="handle"></div>
                    </div>
                </div>
            </template>
        </template>
    </ContainerFloating>
</template>

<style>
    .container-floating.topbar {
        top: 3rem;
        left: 0;
    }

    .container-floating.horizontal .fluid-button + .fluid-button {
        margin-left: -1px;
    }

    .settings-group .grid-type-btn svg {
        height: 1.125rem;
        width: 1.125rem;
    }

    .settings-group.horizontal {
        align-items: center;
    }
</style>
